/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Paperclip, Eye, Download } from "lucide-react";
import { API } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import DocumentViewer from "./DocumentViewer";

const CATEGORY_LABEL = {
  cartao_cidadao: "Cartão de Cidadão",
  cartao_aluno: "Cartão de aluno",
  medical: "Certificado médico",
  licenca: "Licença PU ou PPL",
  licenca_pu: "Licença PU",
  profcheck: "Prova com examinador (ProfCheck)",
  outro: "Outro",
  navegabilidade: "Navegabilidade",
  radio: "Licença de rádio",
  seguro: "Seguro",
};

/**
 * Botão dos documentos num cartão (instrutor ou piloto). Só aparece quando o titular tem pelo menos um documento.
 * Abre a lista: «Ver» pré-visualiza na app (PDF ou imagem) e «Descarregar» guarda o ficheiro.
 * Carregar no botão não abre nem fecha o cartão.
 */
export default function CardDocuments({ ownerKind, ownerId, count }) {
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/di/documents/by-owner/${ownerKind}/${ownerId}`, { withCredentials: true })
      .then((r) => { if (!cancelled) setDocs(r.data || []); })
      .catch((e) => { if (!cancelled) toast.error(e?.response?.data?.detail || "Erro a carregar documentos"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, ownerKind, ownerId]);

  if (!count) return null;
  const downloadUrl = (d) => `${API}/di/documents/${d.id}/download`;

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-slate-600 text-[11px] text-slate-300 hover:bg-slate-800 hover:text-white"
        title={`Documentos (${count})`}
        aria-label={`Ver os ${count} documento(s)`}
        data-testid="card-docs-btn"
      >
        <Paperclip className="w-3 h-3" />
        {count}
      </button>

      <Dialog open={open} onOpenChange={(o) => { if (!o) setOpen(false); }}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg" data-testid="card-docs-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-sm">Documentos</DialogTitle>
          </DialogHeader>
          {loading && <div className="text-xs text-slate-500">A carregar…</div>}
          {!loading && docs.length === 0 && <div className="text-xs text-slate-500">Sem documentos.</div>}
          {!loading && docs.length > 0 && (
            <ul className="divide-y divide-slate-800">
              {docs.map((d) => (
                <li key={d.id} className="flex items-center gap-2 py-2 min-w-0">
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] text-slate-100 font-medium">{CATEGORY_LABEL[d.category] || d.category || "Outro"}</div>
                    {d.notes && <div className="text-[11px] text-slate-300 italic break-words">{d.notes}</div>}
                    {d.uploaded_at && <div className="text-[10px] text-slate-500">carregado em {String(d.uploaded_at).slice(0, 10)}</div>}
                  </div>
                  <button
                    type="button"
                    onClick={() => { setOpen(false); setViewing(d); }}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-600 text-[11px] text-slate-200 hover:bg-slate-800"
                    data-testid="card-docs-view"
                  >
                    <Eye className="w-3 h-3" /> Ver
                  </button>
                  <a
                    href={downloadUrl(d)}
                    download={d.filename}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-600 text-[11px] text-slate-200 hover:bg-slate-800"
                    data-testid="card-docs-download"
                  >
                    <Download className="w-3 h-3" /> Descarregar
                  </a>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>

      {viewing && (
        <DocumentViewer doc={viewing} downloadUrl={downloadUrl(viewing)} onClose={() => setViewing(null)} />
      )}
    </>
  );
}
